import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MessageSquare,
  MessageCircle,
  Loader2,
  RefreshCw,
  Search,
  Shield,
  Send,
  X,
  Inbox,
  Users,
  UserPlus,
  LogOut,
  Plus,
  ArrowLeft,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';
import { getAppSocket } from '@/services/socketRegistry';

interface WaIdentityChoice {
  kind: 'client' | 'user';
  id: string;
  name: string;
  label: string;
}

interface Conversation {
  id: string;
  chatId?: string;
  contactPhone: string | null;
  contactName: string | null;
  isGroup?: boolean;
  groupName?: string | null;
  clientId: string | null;
  identityChoices?: WaIdentityChoice[];
  assignedToId: string | null;
  lastMessageAt: string;
  lastMessageText: string | null;
  unreadCount: number;
  lastMessage: WhatsappMessage | null;
}

interface WhatsappMessage {
  id: string;
  conversationId: string;
  authorType: 'CLIENT' | 'BUSINESS_USER' | 'BUSINESS_AI';
  authorId: string | null;
  content: string;
  messageType: string;
  mediaPath?: string | null;
  mediaMimeType?: string | null;
  mediaUrl?: string | null;
  transcription?: string | null;
  aiResponse?: string | null;
  processed: boolean;
  timestamp: string;
  ack?: number;
}

interface OpenWaContact {
  id: string;
  name: string | null;
  pushName: string | null;
  number: string | null;
  isMyContact?: boolean;
}

function formatPhoneDisplay(raw: string | null | undefined) {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  // User-part LID (13+ cifre, spesso 14-17) non è un numero di telefono
  if (digits.length >= 14 || (digits.length >= 13 && !digits.startsWith('39'))) return '';
  if (digits.startsWith('39') && digits.length >= 11 && digits.length <= 13) {
    const rest = digits.slice(2);
    if (rest.length === 10) return `+39 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
    return `+39 ${rest}`;
  }
  if (digits.length < 8 || digits.length > 13) return '';
  return `+${digits}`;
}

/** Mai mostrare JID grezzi (@lid / @c.us / @g.us) in UI. */
function conversationSubtitle(c: Conversation) {
  if (c.isGroup) return '';
  const phone = formatPhoneDisplay(c.contactPhone);
  if (phone) return phone;
  if (c.chatId?.endsWith('@c.us')) {
    return formatPhoneDisplay(c.chatId.replace(/@c\.us$/i, ''));
  }
  return '';
}

function isPhoneStubConversation(c: Conversation) {
  if (c.isGroup) return false;
  const name = c.contactName || '';
  const letters = name.replace(/[^\p{L}]/gu, '');
  if (letters) return false;
  const preview = (c.lastMessageText || '').trim();
  return !preview || preview === '[Messaggio]';
}

function conversationLabel(c: Conversation) {
  if (c.isGroup) return c.groupName || 'Gruppo';
  const phone = formatPhoneDisplay(c.contactPhone);
  if (c.contactName && !c.contactName.includes('@')) return c.contactName;
  if (phone) return phone;
  return 'Chat';
}

function initials(label: string) {
  const parts = label.replace(/[^\p{L}\p{N}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function daySeparatorLabel(iso: string) {
  const d = new Date(iso);
  const today = startOfDay(new Date());
  const day = startOfDay(d);
  const diffDays = Math.round((today - day) / 86400000);
  if (diffDays === 0) return 'Oggi';
  if (diffDays === 1) return 'Ieri';
  return d.toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}

function listTimeLabel(iso: string) {
  const d = new Date(iso);
  const today = startOfDay(new Date());
  const day = startOfDay(d);
  const diffDays = Math.round((today - day) / 86400000);
  if (diffDays === 0) {
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Ieri';
  if (diffDays < 7) return d.toLocaleDateString('it-IT', { weekday: 'long' });
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function bubbleTime(iso: string) {
  return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

/** wwebjs ack: -1 error, 0 pending, 1 server, 2 delivered, 3 read, 4 played */
function WhatsAppAckTicks({ ack }: { ack: number | undefined }) {
  const n = typeof ack === 'number' ? ack : 0;
  if (n < 0) {
    return <span className="ml-0.5 text-[10px] text-red-500" title="Non consegnato">!</span>;
  }
  if (n >= 3) {
    return <CheckCheck className="ml-0.5 inline h-3 w-3 text-[#53bdeb]" aria-label="Letto" />;
  }
  if (n === 2) {
    return <CheckCheck className="ml-0.5 inline h-3 w-3 text-[#667781]" aria-label="Consegnato" />;
  }
  return <Check className="ml-0.5 inline h-3 w-3 text-[#667781]" aria-label="Inviato" />;
}

function messageDisplayText(m: WhatsappMessage) {
  if (m.transcription?.trim()) return m.transcription.trim();
  if (m.messageType === 'AUDIO' && (!m.content || m.content === '[Audio]')) {
    return m.transcription?.trim() || '[Audio]';
  }
  return m.content || '';
}

function isImageMessage(m: WhatsappMessage) {
  return (
    m.messageType === 'IMAGE' ||
    Boolean(m.mediaMimeType?.startsWith('image/')) ||
    (m.content === '[Immagine]' && Boolean(m.mediaPath || m.mediaUrl))
  );
}

function useAuthMediaSrc(messageId: string | null) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!messageId) {
      setSrc(null);
      setFailed(false);
      setLoading(false);
      return;
    }
    let revoked: string | null = null;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setSrc(null);
    const token = localStorage.getItem('accessToken');
    fetch(`/api/whatsapp/media/${messageId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error('media');
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        revoked = URL.createObjectURL(blob);
        setSrc(revoked);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [messageId]);

  return { src, failed, loading };
}

function AuthMediaImage({
  messageId,
  alt,
  onOpen,
}: {
  messageId: string;
  alt: string;
  onOpen: () => void;
}) {
  const { src, failed, loading } = useAuthMediaSrc(messageId);

  if (failed) {
    return <p className="mt-1 text-xs opacity-80">[Immagine non disponibile]</p>;
  }
  if (loading || !src) {
    return (
      <div className="mt-1 flex h-32 w-48 items-center justify-center rounded-md bg-black/5 text-xs opacity-70">
        <Loader2 className="mr-1 h-3 w-3 animate-spin" /> Carico…
      </div>
    );
  }
  return (
    <button type="button" onClick={onOpen} className="mt-1 block w-full text-left">
      <img src={src} alt={alt} className="max-h-72 max-w-full cursor-zoom-in rounded-md object-contain" />
    </button>
  );
}

function ImageGalleryModal({
  messageIds,
  index,
  onClose,
  onIndexChange,
}: {
  messageIds: string[];
  index: number;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}) {
  const currentId = messageIds[index] || null;
  const { src, failed, loading } = useAuthMediaSrc(currentId);
  const hasPrev = index > 0;
  const hasNext = index < messageIds.length - 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onIndexChange(index - 1);
      if (e.key === 'ArrowRight' && hasNext) onIndexChange(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasNext, hasPrev, index, onClose, onIndexChange]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/90"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="text-sm opacity-80">
          {messageIds.length ? `${index + 1} / ${messageIds.length}` : ''}
        </span>
        <button
          type="button"
          className="rounded-full p-2 hover:bg-white/10"
          onClick={onClose}
          aria-label="Chiudi"
        >
          <X className="h-6 w-6" />
        </button>
      </div>
      <div
        className="relative flex flex-1 items-center justify-center px-12 pb-8"
        onClick={(e) => e.stopPropagation()}
      >
        {hasPrev ? (
          <button
            type="button"
            className="absolute left-2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            onClick={() => onIndexChange(index - 1)}
            aria-label="Precedente"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
        ) : null}
        {loading || !src ? (
          <div className="flex items-center gap-2 text-white/80">
            <Loader2 className="h-6 w-6 animate-spin" />
            {failed ? 'Immagine non disponibile' : 'Carico…'}
          </div>
        ) : (
          <img src={src} alt="media" className="max-h-[85vh] max-w-[92vw] object-contain" />
        )}
        {hasNext ? (
          <button
            type="button"
            className="absolute right-2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            onClick={() => onIndexChange(index + 1)}
            aria-label="Successiva"
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function WhatsAppPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN';
  const canManageGroups = user?.role === 'ADMIN' || user?.role === 'WHATSAPP_MANAGER';

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<WhatsappMessage[]>([]);
  const [isSidebarLoading, setSidebarLoading] = useState(false);
  const [isMessagesLoading, setMessagesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [linkingIdentity, setLinkingIdentity] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupParticipants, setNewGroupParticipants] = useState('');
  const [addParticipants, setAddParticipants] = useState('');
  const [listFilter, setListFilter] = useState<'all' | 'direct' | 'groups'>('all');
  const [syncing, setSyncing] = useState(false);
  const [composerMode, setComposerMode] = useState<'list' | 'new-chat' | 'new-group'>('list');
  const [contacts, setContacts] = useState<OpenWaContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactQuery, setContactQuery] = useState('');
  const [openingChat, setOpeningChat] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  const conversationsRef = useRef(conversations);
  selectedIdRef.current = selectedConversationId;
  conversationsRef.current = conversations;

  const scrollMessagesToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const run = () => {
      el.scrollTop = el.scrollHeight;
      messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
    };
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  }, []);

  const fetchWithToken = useMemo(
    () => (input: RequestInfo, init?: RequestInit) => {
      const token = localStorage.getItem('accessToken');
      return fetch(input, {
        ...(init || {}),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(init?.headers || {}),
        },
      });
    },
    []
  );

  const loadConversations = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setSidebarLoading(true);
      try {
        const res = await fetchWithToken('/api/whatsapp/conversations');
        if (!res.ok) throw new Error('Errore caricamento conversazioni');
        const data = await res.json();
        if (data.success) setConversations(data.data || []);
      } catch (error) {
        console.error(error);
        if (!opts?.silent) toast.error('Impossibile caricare le conversazioni');
      } finally {
        if (!opts?.silent) setSidebarLoading(false);
      }
    },
    [fetchWithToken]
  );

  const loadMessages = useCallback(
    async (conversationId: string, opts?: { silent?: boolean }) => {
      if (!opts?.silent) setMessagesLoading(true);
      try {
        const res = await fetchWithToken(`/api/whatsapp/conversations/${conversationId}/messages`);
        if (!res.ok) throw new Error('Errore caricamento messaggi');
        const data = await res.json();
        if (data.success) setMessages(data.data || []);
      } catch (error) {
        console.error(error);
        if (!opts?.silent) toast.error('Impossibile caricare i messaggi');
      } finally {
        if (!opts?.silent) setMessagesLoading(false);
      }
    },
    [fetchWithToken]
  );

  const handleConversationSelect = useCallback(
    async (conversationId: string) => {
      setSelectedConversationId(conversationId);
      setReplyText('');
      setComposerMode('list');
      setGalleryIndex(null);
      setConversations((prev) =>
        prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c))
      );
      void loadMessages(conversationId);
      try {
        await fetchWithToken(`/api/whatsapp/conversations/${conversationId}/read`, {
          method: 'POST',
          body: '{}',
        });
      } catch {
        /* ignore */
      }
      void loadConversations({ silent: true });
    },
    [fetchWithToken, loadConversations, loadMessages]
  );

  const handleLinkIdentity = useCallback(
    async (choice: WaIdentityChoice) => {
      if (!selectedConversationId || linkingIdentity) return;
      setLinkingIdentity(true);
      try {
        const res = await fetchWithToken(`/api/whatsapp/conversations/${selectedConversationId}/link-identity`, {
          method: 'POST',
          body: JSON.stringify({ kind: choice.kind, id: choice.id }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false) {
          throw new Error(data.message || 'Associazione non riuscita');
        }
        toast.success(`Numero associato a ${choice.name}`);
        await loadConversations({ silent: true });
      } catch (error: any) {
        toast.error(error?.message || 'Associazione non riuscita');
      } finally {
        setLinkingIdentity(false);
      }
    },
    [fetchWithToken, linkingIdentity, loadConversations, selectedConversationId]
  );

  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      if (!confirm('Eliminare questa conversazione?')) return;
      try {
        const response = await fetch(`/api/whatsapp/conversations/${conversationId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        });
        if (!response.ok) throw new Error("Errore durante l'eliminazione");
        toast.success('Conversazione eliminata');
        void loadConversations({ silent: true });
        if (selectedConversationId === conversationId) {
          setSelectedConversationId(null);
          setMessages([]);
        }
      } catch (error: any) {
        toast.error(error.message || 'Errore eliminazione');
      }
    },
    [selectedConversationId, loadConversations]
  );

  const handleSendMessage = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!selectedConversationId || !replyText.trim() || sending) return;
      const conversation = conversations.find((conv) => conv.id === selectedConversationId);
      if (!conversation) return toast.error('Conversazione non trovata');

      setSending(true);
      try {
        const res = await fetchWithToken('/api/whatsapp/send', {
          method: 'POST',
          body: JSON.stringify({
            to: conversation.chatId || conversation.contactPhone,
            text: replyText,
          }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.message || 'Errore invio messaggio');
        setReplyText('');
        void loadMessages(selectedConversationId, { silent: true });
        void loadConversations({ silent: true });
      } catch (error) {
        console.error(error);
        toast.error("Errore durante l'invio del messaggio");
      } finally {
        setSending(false);
      }
    },
    [conversations, fetchWithToken, loadConversations, loadMessages, replyText, selectedConversationId, sending]
  );

  const handleSyncInbox = useCallback(async () => {
    if (!canManageGroups || syncing) return;
    setSyncing(true);
    try {
      toast.info('Reload pulito inbox OpenWA…');
      const res = await fetchWithToken('/api/whatsapp/openwa/chats/reset-sync', {
        method: 'POST',
        body: JSON.stringify({ maxChats: 80, messageLimit: 40, downloadMediaLimit: 5 }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Sync fallita');
      const d = data.data || {};
      toast.success(
        `Reload: ${d.direct || 0} 1:1 · ${d.groups || 0} gruppi · ${d.messagesUpserted || 0} msg · ripristini ${d.restoredEnrichments || 0}`
      );
      void loadConversations({ silent: true });
      if (selectedConversationId) void loadMessages(selectedConversationId, { silent: true });
    } catch (error: any) {
      toast.error(error.message || 'Errore sync inbox');
    } finally {
      setSyncing(false);
    }
  }, [canManageGroups, fetchWithToken, loadConversations, loadMessages, selectedConversationId, syncing]);

  const loadContacts = useCallback(async () => {
    if (!canManageGroups) return;
    setContactsLoading(true);
    try {
      const res = await fetchWithToken('/api/whatsapp/openwa/contacts');
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Rubrica non disponibile');
      setContacts(Array.isArray(data.data) ? data.data : []);
    } catch (error: any) {
      toast.error(error.message || 'Errore rubrica');
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, [canManageGroups, fetchWithToken]);

  const openComposerNewChat = useCallback(() => {
    setComposerMode('new-chat');
    setContactQuery(searchTerm);
    void loadContacts();
  }, [loadContacts, searchTerm]);

  const handleFindOrOpen = useCallback(
    async (query: string) => {
      const q = query.trim();
      if (!q) return toast.error('Inserisci nome o numero');
      setOpeningChat(true);
      try {
        const res = await fetchWithToken('/api/whatsapp/chats/find-or-open', {
          method: 'POST',
          body: JSON.stringify({ query: q }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.message || 'Impossibile aprire chat');
        await loadConversations({ silent: true });
        setComposerMode('list');
        setContactQuery('');
        setSearchTerm('');
        handleConversationSelect(data.data.id);
      } catch (error: any) {
        toast.error(error.message || 'Errore apertura chat');
      } finally {
        setOpeningChat(false);
      }
    },
    [fetchWithToken, handleConversationSelect, loadConversations]
  );

  const handleCreateGroup = useCallback(async () => {
    if (!newGroupName.trim()) return toast.error('Nome gruppo obbligatorio');
    const participants = newGroupParticipants
      .split(/[,\s]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (participants.length === 0) return toast.error('Almeno un partecipante');
    try {
      const res = await fetchWithToken('/api/whatsapp/openwa/groups', {
        method: 'POST',
        body: JSON.stringify({ name: newGroupName.trim(), participants }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Creazione fallita');
      toast.success('Gruppo creato');
      setNewGroupName('');
      setNewGroupParticipants('');
      setComposerMode('list');
      await fetchWithToken('/api/whatsapp/openwa/chats/sync', {
        method: 'POST',
        body: JSON.stringify({ maxChats: 20, messageLimit: 5 }),
      });
      void loadConversations({ silent: true });
    } catch (error: any) {
      toast.error(error.message || 'Errore creazione gruppo');
    }
  }, [fetchWithToken, loadConversations, newGroupName, newGroupParticipants]);

  const handleAddParticipants = useCallback(async () => {
    const conversation = conversations.find((c) => c.id === selectedConversationId);
    if (!conversation?.isGroup || !conversation.chatId) return;
    const participants = addParticipants
      .split(/[,\s]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!participants.length) return toast.error('Indica almeno un numero');
    try {
      const res = await fetchWithToken(
        `/api/whatsapp/openwa/groups/${encodeURIComponent(conversation.chatId)}/participants`,
        { method: 'POST', body: JSON.stringify({ participants }) }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Add fallito');
      toast.success('Partecipanti aggiunti');
      setAddParticipants('');
    } catch (error: any) {
      toast.error(error.message || 'Errore add partecipanti');
    }
  }, [addParticipants, conversations, fetchWithToken, selectedConversationId]);

  const handleLeaveGroup = useCallback(async () => {
    const conversation = conversations.find((c) => c.id === selectedConversationId);
    if (!conversation?.isGroup || !conversation.chatId) return;
    if (!confirm('Uscire dal gruppo WhatsApp?')) return;
    try {
      const res = await fetchWithToken(
        `/api/whatsapp/openwa/groups/${encodeURIComponent(conversation.chatId)}/leave`,
        { method: 'POST' }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Leave fallito');
      toast.success('Uscito dal gruppo');
      setSelectedConversationId(null);
      void loadConversations({ silent: true });
    } catch (error: any) {
      toast.error(error.message || 'Errore leave gruppo');
    }
  }, [conversations, fetchWithToken, loadConversations, selectedConversationId]);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    let cancelled = false;
    let bound: ReturnType<typeof getAppSocket> = null;
    let onConnect: (() => void) | null = null;

    const onInbound = (payload: { conversationId?: string; chatId?: string }) => {
      void loadConversations({ silent: true });
      const openId = selectedIdRef.current;
      if (!openId) return;
      const open = conversationsRef.current.find((c) => c.id === openId);
      const sameConversation =
        !payload?.conversationId ||
        payload.conversationId === openId ||
        !!(payload?.chatId && open?.chatId && payload.chatId === open.chatId);
      if (sameConversation) {
        void loadMessages(openId, { silent: true });
      }
    };

    const bind = (socket: NonNullable<ReturnType<typeof getAppSocket>>) => {
      bound = socket;
      onConnect = () => socket.emit('subscribe:whatsapp');
      onConnect();
      socket.on('connect', onConnect);
      socket.on('whatsapp:message', onInbound);
    };

    const existing = getAppSocket();
    if (existing) bind(existing);
    const timer = window.setInterval(() => {
      if (cancelled || bound) return;
      const s = getAppSocket();
      if (s) bind(s);
    }, 400);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      if (bound) {
        bound.off('whatsapp:message', onInbound);
        if (onConnect) bound.off('connect', onConnect);
      }
    };
  }, [loadConversations, loadMessages]);

  useEffect(() => {
    if (!selectedConversationId) return;
    const tick = () => {
      if (document.hidden) return;
      void loadMessages(selectedConversationId, { silent: true });
      void loadConversations({ silent: true });
    };
    const t = window.setInterval(tick, 12000);
    return () => window.clearInterval(t);
  }, [selectedConversationId, loadConversations, loadMessages]);

  useEffect(() => {
    if (!selectedConversationId || isMessagesLoading) return;
    // Primo paint: vai subito in fondo (auto). Il secondo click funzionava perché il DOM era già pronto.
    scrollMessagesToBottom('auto');
    const t = window.setTimeout(() => scrollMessagesToBottom('auto'), 80);
    return () => window.clearTimeout(t);
  }, [messages, selectedConversationId, isMessagesLoading, scrollMessagesToBottom]);

  const filteredConversations = useMemo(() => {
    let list = [...conversations].sort(
      (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
    );
    if (listFilter === 'direct') list = list.filter((c) => !c.isGroup);
    if (listFilter === 'groups') list = list.filter((c) => Boolean(c.isGroup));
    list = list.filter((c) => !isPhoneStubConversation(c));
    if (!searchTerm.trim()) return list;
    const term = searchTerm.toLowerCase();
    return list.filter((conv) => {
      const label = conv.isGroup
        ? `${conv.groupName || ''} ${conv.chatId || ''}`
        : `${conv.contactName || ''} ${conv.contactPhone || ''} ${conv.chatId || ''}`;
      return label.toLowerCase().includes(term) || (conv.lastMessageText || '').toLowerCase().includes(term);
    });
  }, [conversations, searchTerm, listFilter]);

  const imageMessageIds = useMemo(
    () => messages.filter(isImageMessage).map((m) => m.id),
    [messages]
  );

  const contactHits = useMemo(() => {
    const q = contactQuery.trim().toLowerCase();
    const digits = contactQuery.replace(/\D/g, '');
    const fromInbox = conversations
      .filter((c) => !c.isGroup)
      .map((c) => ({
        key: `conv-${c.id}`,
        label: conversationLabel(c),
        subtitle: c.contactPhone || c.chatId || '',
        query: c.contactPhone || c.contactName || c.chatId || '',
      }));
    const fromRubrica = contacts.map((c) => ({
      key: `ct-${c.id}`,
      label: c.name || c.pushName || c.number || c.id,
      subtitle: c.number || c.id,
      query: c.number || c.name || c.pushName || c.id,
    }));
    const merged = [...fromInbox, ...fromRubrica];
    const seen = new Set<string>();
    return merged.filter((row) => {
      const norm = `${row.label}|${row.subtitle}`.toLowerCase();
      if (seen.has(norm)) return false;
      seen.add(norm);
      if (!q) return true;
      const hay = `${row.label} ${row.subtitle}`.toLowerCase();
      const rowDigits = row.subtitle.replace(/\D/g, '');
      return hay.includes(q) || (digits.length >= 3 && rowDigits.includes(digits));
    }).slice(0, 40);
  }, [contactQuery, contacts, conversations]);

  const selectedConversation = useMemo(
    () => conversations.find((conv) => conv.id === selectedConversationId) || null,
    [conversations, selectedConversationId]
  );

  const messageTimeline = useMemo(() => {
    const items: Array<{ type: 'sep'; key: string; label: string } | { type: 'msg'; key: string; message: WhatsappMessage }> =
      [];
    let lastDay: number | null = null;
    for (const message of messages) {
      const day = startOfDay(new Date(message.timestamp));
      if (lastDay !== day) {
        items.push({ type: 'sep', key: `sep-${day}`, label: daySeparatorLabel(message.timestamp) });
        lastDay = day;
      }
      items.push({ type: 'msg', key: message.id, message });
    }
    return items;
  }, [messages]);

  if (!user) return null;

  const headerTitle = selectedConversation ? conversationLabel(selectedConversation) : '';

  return (
    <div className="flex h-full min-h-0 w-full flex-1 overflow-hidden border-0 bg-[#f0f2f5]">
      {/* Lista chat */}
      <aside className="flex h-full w-full max-w-md shrink-0 flex-col overflow-hidden border-r border-[#d1d7db] bg-white lg:w-[380px]">
        <div className="flex items-center justify-between bg-[#f0f2f5] px-4 py-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-[#00a884]" />
            <h1 className="text-lg font-semibold text-[#111b21]">Chat</h1>
          </div>
          <div className="flex items-center gap-1">
            {canManageGroups ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Nuova chat"
                  className="text-[#54656f]"
                  onClick={openComposerNewChat}
                >
                  <Plus className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Nuovo gruppo"
                  className="text-[#54656f]"
                  onClick={() => setComposerMode('new-group')}
                >
                  <Users className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Reload pulito inbox OpenWA"
                  className="text-[#54656f]"
                  disabled={syncing}
                  onClick={handleSyncInbox}
                >
                  <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="text-[#54656f]"
                title="Aggiorna lista"
                onClick={() => void loadConversations()}
              >
                <RefreshCw className={`h-4 w-4 ${isSidebarLoading ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
        </div>

        {composerMode === 'new-chat' ? (
          <div className="flex flex-1 flex-col overflow-hidden bg-white">
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Button variant="ghost" size="icon" onClick={() => setComposerMode('list')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <p className="text-sm font-semibold">Nuova chat</p>
            </div>
            <div className="border-b px-3 py-2">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#667781]" />
                <Input
                  autoFocus
                  placeholder="Cerca nome o numero"
                  className="border-0 bg-[#f0f2f5] pl-10 shadow-none focus-visible:ring-0"
                  value={contactQuery}
                  onChange={(e) => setContactQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleFindOrOpen(contactQuery);
                  }}
                />
              </div>
              <Button
                size="sm"
                className="mt-2 w-full bg-[#00a884] hover:bg-[#008f72]"
                disabled={openingChat || !contactQuery.trim()}
                onClick={() => void handleFindOrOpen(contactQuery)}
              >
                {openingChat ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
                Apri / continua chat
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {contactsLoading ? (
                <div className="flex items-center justify-center py-8 text-sm text-[#667781]">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Rubrica…
                </div>
              ) : contactHits.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-[#667781]">Nessun contatto</p>
              ) : (
                <ul>
                  {contactHits.map((row) => (
                    <li key={row.key}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-[#f5f6f6]"
                        onClick={() => void handleFindOrOpen(row.query)}
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#dfe5e7] text-xs font-semibold text-[#54656f]">
                          {initials(row.label)}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-[#111b21]">{row.label}</span>
                          <span className="block truncate text-xs text-[#667781]">{row.subtitle}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : composerMode === 'new-group' ? (
          <div className="flex flex-1 flex-col overflow-hidden bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => setComposerMode('list')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <p className="text-sm font-semibold">Nuovo gruppo</p>
            </div>
            <div className="space-y-2">
              <Input placeholder="Nome gruppo" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} />
              <Input
                placeholder="Partecipanti (tel separati)"
                value={newGroupParticipants}
                onChange={(e) => setNewGroupParticipants(e.target.value)}
              />
              <Button className="w-full bg-[#00a884] hover:bg-[#008f72]" onClick={handleCreateGroup}>
                Crea gruppo
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-2 px-3 py-2">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#667781]" />
                <Input
                  placeholder="Cerca o avvia una nuova chat"
                  className="border-0 bg-[#f0f2f5] pl-10 shadow-none focus-visible:ring-0"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => {
                    if (canManageGroups && searchTerm.trim()) openComposerNewChat();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canManageGroups && searchTerm.trim()) {
                      setComposerMode('new-chat');
                      setContactQuery(searchTerm);
                      void loadContacts();
                    }
                  }}
                />
              </div>
              <div className="flex gap-1">
                {(
                  [
                    ['all', 'Tutte'],
                    ['direct', '1:1'],
                    ['groups', 'Gruppi'],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setListFilter(key)}
                    className={`rounded-full px-3 py-1 text-xs ${
                      listFilter === key
                        ? 'bg-[#e7fce3] text-[#008069]'
                        : 'bg-transparent text-[#54656f] hover:bg-[#f0f2f5]'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {isSidebarLoading && conversations.length === 0 ? (
                <div className="flex h-40 items-center justify-center text-[#667781]">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Caricamento…
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex h-40 flex-col items-center justify-center gap-2 text-[#667781]">
                  <Inbox className="h-8 w-8" />
                  <p className="px-6 text-center text-sm">Nessuna conversazione</p>
                </div>
              ) : (
                <ul>
                  {filteredConversations.map((conversation) => {
                    const label = conversationLabel(conversation);
                    const active = selectedConversationId === conversation.id;
                    return (
                      <li key={conversation.id}>
                        <button
                          type="button"
                          className={`flex w-full items-start gap-3 border-b border-[#f0f2f5] px-3 py-3 text-left ${
                            active ? 'bg-[#f0f2f5]' : 'hover:bg-[#f5f6f6]'
                          }`}
                          onClick={() => handleConversationSelect(conversation.id)}
                        >
                          <span className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#dfe5e7] text-sm font-semibold text-[#54656f]">
                            {conversation.isGroup ? <Users className="h-5 w-5" /> : initials(label)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center justify-between gap-2">
                              <span className="truncate text-sm font-medium text-[#111b21]">{label}</span>
                              <span className="shrink-0 text-[11px] text-[#667781]">
                                {listTimeLabel(conversation.lastMessageAt)}
                              </span>
                            </span>
                            <span className="mt-0.5 flex items-center justify-between gap-2">
                              <span className="truncate text-xs text-[#667781]">
                                {conversation.lastMessageText || 'Nessun messaggio'}
                              </span>
                              <span className="flex shrink-0 items-center gap-1">
                                {conversation.unreadCount > 0 ? (
                                  <Badge className="h-5 min-w-5 justify-center rounded-full bg-[#25d366] px-1.5 text-[10px] text-white">
                                    {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                                  </Badge>
                                ) : null}
                                {canManageGroups ? (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    className="rounded p-0.5 text-red-400 hover:bg-red-50 hover:text-red-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void handleDeleteConversation(conversation.id);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.stopPropagation();
                                        void handleDeleteConversation(conversation.id);
                                      }
                                    }}
                                  >
                                    <X className="h-3 w-3" />
                                  </span>
                                ) : null}
                              </span>
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
      </aside>

      {/* Pannello chat */}
      <section className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%224%22 height=%224%22><rect fill=%22%23e5ddd5%22 width=%224%22 height=%224%22/><circle cx=%221%22 cy=%221%22 r=%220.6%22 fill=%22%23d6d0c8%22 opacity=%220.35%22/></svg>')] bg-[#e5ddd5]">
        {!selectedConversation ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 bg-[#f0f2f5] px-6 text-center text-[#667781]">
            <MessageCircle className="h-16 w-16 opacity-40" />
            <p className="max-w-sm text-sm">Seleziona una chat o avviane una nuova dalla rubrica.</p>
            {!isAdmin && (
              <div className="mt-2 flex items-center gap-2 text-xs text-amber-600">
                <Shield className="h-4 w-4" />
                <span>Vedi solo le conversazioni a te associate.</span>
              </div>
            )}
          </div>
        ) : (
          <>
            <header className="flex items-center justify-between border-b border-[#d1d7db] bg-[#f0f2f5] px-4 py-2.5">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#dfe5e7] text-sm font-semibold text-[#54656f]">
                  {selectedConversation.isGroup ? <Users className="h-5 w-5" /> : initials(headerTitle)}
                </span>
                <div className="min-w-0">
                  <h2 className="truncate text-sm font-semibold text-[#111b21]">{headerTitle}</h2>
                  <p className="truncate text-xs text-[#667781]">
                    {conversationSubtitle(selectedConversation) ||
                      (selectedConversation.isGroup ? 'Gruppo WhatsApp' : 'Contatto WhatsApp')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {canManageGroups && selectedConversation.isGroup ? (
                  <>
                    <Input
                      className="h-8 w-36 bg-white text-xs"
                      placeholder="Aggiungi tel"
                      value={addParticipants}
                      onChange={(e) => setAddParticipants(e.target.value)}
                    />
                    <Button size="sm" variant="outline" onClick={handleAddParticipants}>
                      <UserPlus className="mr-1 h-3 w-3" /> Add
                    </Button>
                    <Button size="sm" variant="destructive" onClick={handleLeaveGroup}>
                      <LogOut className="mr-1 h-3 w-3" /> Leave
                    </Button>
                  </>
                ) : null}
                <Button variant="ghost" size="icon" onClick={() => setSelectedConversationId(null)}>
                  <X className="h-5 w-5 text-[#54656f]" />
                </Button>
              </div>
            </header>
            {(selectedConversation.identityChoices?.length || 0) > 1 && (
              <div className="border-b border-amber-200 bg-amber-50 px-4 py-3">
                <p className="mb-2 text-sm text-amber-950">
                  Questo numero è su più schede. Quale nome associare su WhatsApp?
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedConversation.identityChoices!.map((choice) => (
                    <Button
                      key={`${choice.kind}-${choice.id}`}
                      size="sm"
                      variant="outline"
                      disabled={linkingIdentity}
                      onClick={() => void handleLinkIdentity(choice)}
                    >
                      {choice.name}
                      <span className="ml-1 text-xs text-[#667781]">({choice.label})</span>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesScrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-3 md:px-10">
              {isMessagesLoading && messages.length === 0 ? (
                <div className="flex h-full items-center justify-center text-[#667781]">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Caricamento messaggi…
                </div>
              ) : messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-[#667781]">
                  <MessageCircle className="h-8 w-8" />
                  <p className="mt-2 text-sm">Nessun messaggio</p>
                </div>
              ) : (
                <div className="mx-auto flex min-h-full max-w-3xl flex-col justify-end gap-1">
                  {messageTimeline.map((item) =>
                    item.type === 'sep' ? (
                      <div key={item.key} className="my-3 flex justify-center">
                        <span className="rounded-lg bg-[#ffeecd] px-3 py-1 text-[11px] font-medium text-[#54656f] shadow-sm">
                          {item.label}
                        </span>
                      </div>
                    ) : (
                      <div
                        key={item.key}
                        className={`flex ${item.message.authorType === 'CLIENT' ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          className={`relative max-w-[85%] rounded-lg px-2.5 py-1.5 text-sm shadow-sm md:max-w-[70%] ${
                            item.message.authorType === 'CLIENT'
                              ? 'rounded-tl-none bg-white text-[#111b21]'
                              : item.message.authorType === 'BUSINESS_AI'
                                ? 'rounded-tr-none bg-[#e0e7ff] text-[#111b21]'
                                : 'rounded-tr-none bg-[#d9fdd3] text-[#111b21]'
                          }`}
                        >
                          {isImageMessage(item.message) ? (
                            <AuthMediaImage
                              messageId={item.message.id}
                              alt="allegato"
                              onOpen={() => {
                                const idx = imageMessageIds.indexOf(item.message.id);
                                setGalleryIndex(idx >= 0 ? idx : 0);
                              }}
                            />
                          ) : null}
                          {(() => {
                            const text = messageDisplayText(item.message);
                            if (!text || (isImageMessage(item.message) && text === '[Immagine]')) return null;
                            return (
                              <p className="whitespace-pre-wrap break-words leading-relaxed">{text}</p>
                            );
                          })()}
                          <p className="mt-0.5 flex items-center justify-end gap-0.5 text-[10px] text-[#667781]">
                            <span>{bubbleTime(item.message.timestamp)}</span>
                            {item.message.authorType !== 'CLIENT' ? (
                              <WhatsAppAckTicks ack={item.message.ack} />
                            ) : null}
                          </p>
                        </div>
                      </div>
                    )
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            <footer className="border-t border-[#d1d7db] bg-[#f0f2f5] px-3 py-2">
              <form onSubmit={handleSendMessage} className="mx-auto flex max-w-3xl items-end gap-2">
                <input
                  className="min-h-[42px] flex-1 rounded-lg border-0 bg-white px-4 py-2.5 text-sm text-[#111b21] shadow-sm outline-none placeholder:text-[#667781]"
                  placeholder="Scrivi un messaggio"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void handleSendMessage(e as unknown as React.FormEvent);
                    }
                  }}
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={sending || !replyText.trim()}
                  className="h-[42px] w-[42px] rounded-full bg-[#00a884] hover:bg-[#008f72]"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </footer>
          </>
        )}
      </section>
      {galleryIndex !== null && imageMessageIds.length > 0 ? (
        <ImageGalleryModal
          messageIds={imageMessageIds}
          index={Math.min(galleryIndex, imageMessageIds.length - 1)}
          onClose={() => setGalleryIndex(null)}
          onIndexChange={setGalleryIndex}
        />
      ) : null}
    </div>
  );
}
